import React, { useState, useEffect, useMemo } from 'react';
import { receptionsAPI } from '@/lib/api';
import { 
  FileText, Search, Filter, Calendar, Package, User, 
  ChevronDown, ChevronUp, Download, RefreshCw, Truck, Hash,
  Pencil, Plus, Trash2, X, Check, Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const AdminReceptionsPage = () => {
  const [receptions, setReceptions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReception, setExpandedReception] = useState(null);
  
  // Filtros
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Modal de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReception, setEditingReception] = useState(null);
  const [editForm, setEditForm] = useState({
    supplier: '',
    invoice_number: '',
    notes: '',
    items: []
  });
  const [supplierItems, setSupplierItems] = useState([]);
  const [loadingSupplierItems, setLoadingSupplierItems] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [receptionsRes, suppliersRes] = await Promise.all([
        receptionsAPI.getAll({ limit: 200 }),
        receptionsAPI.getSuppliers()
      ]);
      setReceptions(receptionsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar items del proveedor cuando se cambia en el formulario de edición
  const loadSupplierItems = async (supplier) => {
    if (!supplier) return;
    setLoadingSupplierItems(true);
    try {
      const response = await receptionsAPI.getItemsBySupplier(supplier);
      setSupplierItems(response.data);
    } catch (error) {
      console.error('Error loading supplier items:', error);
    } finally {
      setLoadingSupplierItems(false);
    }
  };

  // Abrir modal de edición
  const openEditDialog = async (reception) => {
    setEditingReception(reception);
    setEditForm({
      supplier: reception.supplier,
      invoice_number: reception.invoice_number,
      notes: reception.notes || '',
      items: reception.items.map(item => ({ ...item }))
    });
    await loadSupplierItems(reception.supplier);
    setEditDialogOpen(true);
  };

  // Cerrar modal
  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingReception(null);
    setEditForm({ supplier: '', invoice_number: '', notes: '', items: [] });
    setSupplierItems([]);
  };

  // Actualizar cantidad de un item
  const updateItemQuantity = (index, quantity) => {
    const newItems = [...editForm.items];
    newItems[index].quantity = parseFloat(quantity) || 0;
    setEditForm({ ...editForm, items: newItems });
  };

  // Eliminar item
  const removeItem = (index) => {
    const newItems = editForm.items.filter((_, i) => i !== index);
    setEditForm({ ...editForm, items: newItems });
  };

  // Agregar item
  const addItem = (item) => {
    // Verificar si ya existe
    if (editForm.items.find(i => i.item_id === item.id)) {
      toast.error('Este producto ya está en la lista');
      return;
    }
    
    const newItems = [...editForm.items, {
      item_id: item.id,
      item_code: item.code,
      item_name: item.name,
      quantity: 1,
      unit: item.unit || 'unidad'
    }];
    setEditForm({ ...editForm, items: newItems });
  };

  // Guardar cambios
  const handleSave = async () => {
    if (!editForm.invoice_number.trim()) {
      toast.error('El número de factura es requerido');
      return;
    }
    if (editForm.items.length === 0) {
      toast.error('Debe haber al menos un producto');
      return;
    }
    if (editForm.items.some(item => item.quantity <= 0)) {
      toast.error('Todas las cantidades deben ser mayores a 0');
      return;
    }

    setSaving(true);
    try {
      await receptionsAPI.update(editingReception.id, {
        supplier: editForm.supplier,
        invoice_number: editForm.invoice_number.trim(),
        notes: editForm.notes.trim() || null,
        items: editForm.items
      });
      
      toast.success('Recepción actualizada correctamente');
      closeEditDialog();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  // Formatear fecha a hora local
  const formatLocalDate = (dateString) => {
    const date = parseISO(dateString);
    return format(date, "dd MMM yyyy, HH:mm", { locale: es });
  };

  // Filtrar recepciones
  const filteredReceptions = useMemo(() => {
    return receptions.filter(reception => {
      if (supplierFilter !== 'all' && reception.supplier !== supplierFilter) {
        return false;
      }
      
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (!reception.invoice_number.toLowerCase().includes(search) &&
            !reception.supplier.toLowerCase().includes(search)) {
          return false;
        }
      }
      
      if (dateFrom) {
        const receptionDate = new Date(reception.created_at);
        const fromDate = new Date(dateFrom);
        if (receptionDate < fromDate) return false;
      }
      
      if (dateTo) {
        const receptionDate = new Date(reception.created_at);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (receptionDate > toDate) return false;
      }
      
      return true;
    });
  }, [receptions, supplierFilter, searchTerm, dateFrom, dateTo]);

  // Estadísticas
  const stats = useMemo(() => {
    return {
      totalReceptions: filteredReceptions.length,
      totalProducts: filteredReceptions.reduce((sum, r) => sum + r.total_items, 0),
      totalQuantity: filteredReceptions.reduce((sum, r) => sum + r.total_quantity, 0),
      uniqueSuppliers: [...new Set(filteredReceptions.map(r => r.supplier))].length
    };
  }, [filteredReceptions]);

  // Exportar a CSV
  const exportToCSV = () => {
    const headers = ['Fecha', 'Hora', 'Proveedor', 'N° Factura', 'Productos', 'Cantidad Total', 'Recibido Por'];
    const rows = filteredReceptions.map(r => [
      format(parseISO(r.created_at), 'dd/MM/yyyy'),
      format(parseISO(r.created_at), 'HH:mm'),
      r.supplier,
      r.invoice_number,
      r.total_items,
      r.total_quantity,
      r.received_by_name
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `recepciones_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Reporte exportado');
  };

  const clearFilters = () => {
    setSupplierFilter('all');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-receptions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Truck className="w-7 h-7 text-primary" />
            Control de Recepciones
          </h1>
          <p className="text-muted-foreground">Historial de ingresos de facturas de proveedores</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="N° factura o proveedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Proveedor</label>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger data-testid="supplier-filter">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {suppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>
                    {supplier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Desde</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="date-from"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Hasta</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="date-to"
            />
          </div>
        </div>
        
        {(supplierFilter !== 'all' || searchTerm || dateFrom || dateTo) && (
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Mostrando {filteredReceptions.length} de {receptions.length} recepciones
            </p>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Recepciones</p>
              <p className="text-2xl font-bold text-slate-900">{stats.totalReceptions}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Productos</p>
              <p className="text-2xl font-bold text-green-600">{stats.totalProducts}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Hash className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Unidades</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalQuantity.toFixed(1)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Truck className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Proveedores</p>
              <p className="text-2xl font-bold text-purple-600">{stats.uniqueSuppliers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de recepciones */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filteredReceptions.length === 0 ? (
          <div className="text-center py-12">
            <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay recepciones que coincidan con los filtros</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredReceptions.map(reception => (
              <div key={reception.id} className="hover:bg-slate-50 transition-colors">
                {/* Header de la recepción */}
                <div className="p-4 flex items-center justify-between">
                  <div 
                    className="flex items-center gap-4 flex-1 cursor-pointer"
                    onClick={() => setExpandedReception(expandedReception === reception.id ? null : reception.id)}
                  >
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{reception.supplier}</p>
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-600">
                          {reception.invoice_number}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatLocalDate(reception.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {reception.received_by_name}
                        </span>
                        {reception.updated_at && (
                          <span className="text-xs text-amber-600">
                            (Editado)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-primary">{reception.total_items} productos</p>
                      <p className="text-sm text-slate-500">{reception.total_quantity} unidades</p>
                    </div>
                    
                    {/* Botón de editar */}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(reception);
                      }}
                      data-testid={`edit-reception-${reception.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    
                    <div 
                      className="cursor-pointer p-1"
                      onClick={() => setExpandedReception(expandedReception === reception.id ? null : reception.id)}
                    >
                      {expandedReception === reception.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Detalle expandido */}
                {expandedReception === reception.id && (
                  <div className="px-4 pb-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      {reception.notes && (
                        <p className="text-sm text-slate-600 mb-4 italic border-l-2 border-primary pl-3">
                          {reception.notes}
                        </p>
                      )}
                      
                      <p className="text-sm font-semibold text-slate-700 mb-3">Detalle de productos:</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-3 font-semibold text-slate-600">Código</th>
                              <th className="text-left py-2 px-3 font-semibold text-slate-600">Producto</th>
                              <th className="text-right py-2 px-3 font-semibold text-slate-600">Cantidad</th>
                              <th className="text-left py-2 px-3 font-semibold text-slate-600">Unidad</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reception.items.map((item) => (
                              <tr key={`${reception.id}-${item.item_id}`} className="border-b border-slate-100 last:border-0">
                                <td className="py-2 px-3 font-mono text-slate-500">{item.item_code}</td>
                                <td className="py-2 px-3 font-medium text-slate-900">{item.item_name}</td>
                                <td className="py-2 px-3 text-right font-bold text-green-600">+{item.quantity}</td>
                                <td className="py-2 px-3 text-slate-500">{item.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-slate-100">
                              <td colSpan="2" className="py-2 px-3 font-semibold text-slate-700">Total</td>
                              <td className="py-2 px-3 text-right font-bold text-primary">{reception.total_quantity}</td>
                              <td className="py-2 px-3"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de edición */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Editar Recepción
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Datos básicos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-supplier">Proveedor</Label>
                <Select 
                  value={editForm.supplier} 
                  onValueChange={(value) => {
                    setEditForm({ ...editForm, supplier: value });
                    loadSupplierItems(value);
                  }}
                >
                  <SelectTrigger id="edit-supplier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier} value={supplier}>
                        {supplier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-invoice">N° Factura</Label>
                <Input
                  id="edit-invoice"
                  value={editForm.invoice_number}
                  onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-notes">Notas</Label>
              <Input
                id="edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Observaciones..."
              />
            </div>

            {/* Productos actuales */}
            <div>
              <Label className="text-base font-semibold">Productos en la recepción</Label>
              <div className="mt-2 space-y-2">
                {editForm.items.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No hay productos</p>
                ) : (
                  editForm.items.map((item, idx) => (
                    <div 
                      key={item.item_id || idx}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.item_name}</p>
                        <p className="text-xs text-slate-500">{item.item_code}</p>
                      </div>
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity(idx, e.target.value)}
                        className="w-24 text-center"
                      />
                      <span className="text-sm text-slate-500 w-16">{item.unit}</span>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeItem(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Agregar productos */}
            <div>
              <Label className="text-base font-semibold">Agregar productos</Label>
              <p className="text-sm text-slate-500 mb-2">
                Selecciona productos de {editForm.supplier} para agregar
              </p>
              
              {loadingSupplierItems ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {supplierItems.map(item => {
                    const isAdded = editForm.items.find(i => i.item_id === item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => !isAdded && addItem(item)}
                        disabled={isAdded}
                        className={`p-2 rounded-lg border text-left text-sm transition-all ${
                          isAdded 
                            ? 'bg-green-50 border-green-300 cursor-not-allowed' 
                            : 'bg-white border-slate-200 hover:border-primary'
                        }`}
                      >
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-xs text-slate-500">{item.code}</p>
                        {isAdded && (
                          <Check className="w-3 h-3 text-green-600 inline ml-1" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReceptionsPage;
